import { Fab } from '@material-ui/core';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import LinearProgress from '@material-ui/core/LinearProgress';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React, { ReactNode, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';

import axios from '../../../axios';
import CustomDialog from '../../../components/UI/CustomDialog/CustomDialog';
import withErrorHandler from '../../../hoc/withErrorHandler/withErrorHandler';
import { IMaterial, ICategory } from '../../../interfaces/domain';
import { IAppState } from '../../../interfaces/state';
import * as actions from '../../../store/actions';

import classes from "./MaterialsList.module.scss";

interface IStateProps {
  materials: IMaterial[];
  loading: boolean;
  isAdmin: boolean;
  categoriesFetched: boolean;
  categories: ICategory[];
}

interface IDispatchProps {
  onFetchMaterials: () => void;
  onSetMaterial: (material: IMaterial) => void;
  onDeleteMaterial: (material: IMaterial) => void;
  onFetchCategories: () => void;
}

interface IProps extends IStateProps, IDispatchProps, RouteComponentProps { }

const MaterialsList: React.FC<IProps> = ({
  loading,
  materials,
  isAdmin,
  onFetchMaterials,
  history,
  onSetMaterial,
  onDeleteMaterial,
  categoriesFetched,
  onFetchCategories,
  categories
}) => {
  const [deleteMaterial, setDeleteMaterial] = useState<IMaterial | null>(null);

  useEffect(() => {
    onFetchMaterials();
    if (!categoriesFetched) {
      onFetchCategories();
    }
  }, [onFetchMaterials, onFetchCategories, categoriesFetched]);

  const addHandler = () => {
    history.push("/materials/add");
  };

  const editHandler = (item: IMaterial) => {
    onSetMaterial(item);
    history.push(`/materials/${item.id}`);
  };

  const deleteHandler = (material: IMaterial) => {
    setDeleteMaterial(material);
  };

  const deleteMaterialHandler = (material: IMaterial | null) => {
    if (material) {
      setDeleteMaterial(null);
      onDeleteMaterial(material);
    }
  };

  const createLink = (link: string, name: string) => {
    if (link.indexOf('/tools') === -1 && link.indexOf('http') === -1) {
      link = `https://${link}`;
    }
    return (
      <p key={link}>
        <a href={link} target="_blank" rel="noopener noreferrer">
          {name}
        </a>
      </p>
    )
  }

  const getCategoryName = (categoryId: number | string | null | undefined) => {
    if (!categoryId) return "Ei kategoriaa";
    const category = categories.find(c => c.id == categoryId);
    return category ? category.name : "Muu";
  };

  const groupedMaterials = materials.reduce((acc, item) => {
    const categoryId = item.categoryId || (item.category && item.category.id) || null;
    const catName = getCategoryName(categoryId);
    if (!acc[catName]) {
      acc[catName] = [];
    }
    acc[catName].push(item);
    return acc;
  }, {} as Record<string, IMaterial[]>);

  let materialsElement: ReactNode = null;
  if (loading) {
    materialsElement = <LinearProgress color="secondary" />;
  } else if (materials && materials.length) {
    materialsElement = (
      <div>
        <CustomDialog
          title="Poista materiaali"
          content="Haluatko varmasti poistaa materiaalin?"
          buttonText="Poista"
          onCancel={() => setDeleteMaterial(null)}
          onOk={() => deleteMaterialHandler(deleteMaterial)}
          open={!!deleteMaterial}
        />
        {Object.entries(groupedMaterials).sort((a, b) => {
          if (a[0] === "Ei kategoriaa") return 1;
          if (b[0] === "Ei kategoriaa") return -1;
          return a[0].localeCompare(b[0]);
        }).map(([categoryName, catMaterials]) => (
          <Accordion key={categoryName}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">{categoryName}</Typography>
            </AccordionSummary>
            <AccordionDetails style={{ overflowX: 'auto', padding: 0 }}>
              <Table className={classes.Table}>
                <TableHead>
                  <TableRow>
                    <TableCell>Nimi</TableCell>
                    <TableCell>Kuvaus</TableCell>
                    <TableCell>Liitteet</TableCell>
                    {isAdmin ? <TableCell align="right"></TableCell> : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {catMaterials.map(item => (
                    <TableRow key={item.id}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>
                        {item.description &&
                          item.description.split("\n").map((item, key) => (
                            <span key={key}>
                              {item}
                              <br />
                            </span>
                          ))}
                      </TableCell>
                      <TableCell>
                        {item.attachments &&
                          item.attachments.map((attachment, key) => createLink(attachment.link, attachment.name))}
                        {item.links && item.links.map((link) => createLink(link, link))}
                      </TableCell>
                      {isAdmin ? (
                        <TableCell align="right">
                          <IconButton
                            aria-label="Muokkaa materiaalia"
                            onClick={() => editHandler(item)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            aria-label="Poista materiaali"
                            onClick={() => deleteHandler(item)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        ))}
      </div>
    );
  } else {
    materialsElement = (
      <Paper elevation={4} className={classes.Paper}>
        <Typography gutterBottom component="b">
          Ei materiaaleja.
        </Typography>
      </Paper>
    );

    if (isAdmin) {
      materialsElement = (
        <Paper elevation={4} className={classes.Paper}>
          <Typography gutterBottom component="p">
            Ei materiaaleja. Aloita lisäämällä.
          </Typography>
          <Button color="primary" onClick={addHandler}>
            Lisää uusi
          </Button>
        </Paper>
      );
    }
  }

  return (
    <React.Fragment>
      {materialsElement}
      {isAdmin ? (
        <Fab
          onClick={addHandler}
          color="primary"
          aria-label="add"
          className={classes.AddButton}
        >
          <AddIcon />
        </Fab>
      ) : null}
    </React.Fragment>
  );
};

const mapStateToProps = (state: IAppState): IStateProps => {
  return {
    materials: state.materials.materials,
    loading: state.materials.loading,
    isAdmin: state.auth.user ? state.auth.user.permissions.koulutusAdmin : false,
    categoriesFetched: state.category.fetched,
    categories: state.category.categories
  };
};

const mapDispatchToProps = (dispatch: any): IDispatchProps => {
  return {
    onFetchMaterials: () => dispatch(actions.fetchMaterials()),
    onSetMaterial: material => dispatch(actions.setMaterial(material)),
    onDeleteMaterial: material => dispatch(actions.deleteMaterial(material)),
    onFetchCategories: () => dispatch(actions.findCategories())
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withErrorHandler(MaterialsList, axios));
